import { Button, ButtonGroup, Checkbox, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import {
  IWorksheetComponent,
  WorksheetAssociatedTestSection,
  WorksheetEquipmentDropdown,
} from 'Components/Worksheets/Core';
import ExternalTestMethods from 'Components/Worksheets/ExternalTestMethods';
import { valueOrDefault } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { MoistureCondition, PenetrationDepthMeasuredBy } from '../types';

let PreparationSectionBase: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const [additionalDescriptionEditable, setAdditionalDescriptionEditable] = useState(false);
  const importedFieldMoistureContentID = useSelector(
    createWorksheetSelector!('AssociatedFieldMoistureContent.ImportedTest.Id')
  ) as Guid;
  const dcpId = useSelector(createWorksheetSelector!('ConePenetrometer.Id')) as Guid;
  const measureId = useSelector(createWorksheetSelector!('Measure.Id')) as Guid;
  const canShowGroundWaterTable = useSelector(
    pipe(createWorksheetSelector!('RecordGroundWaterTableLocation'), valueOrDefault(false))
  );
  const canShowMeasure =
    useSelector(createWorksheetSelector!('PenetrationDepthMeasuredBy')) === PenetrationDepthMeasuredBy.MetreScale;
  const canShowGraduationsInterval =
    useSelector(createWorksheetSelector!('PenetrationDepthMeasuredBy')) ===
    PenetrationDepthMeasuredBy.GraduationsAtUniformIntervals;
  const labMoistureTaken =
    useSelector(createWorksheetSelector!('MoistureCondition')) === MoistureCondition.MoistureContent;
  return (
    <>
      {additionalDescriptionEditable ? (
        <ListFieldSection>
          <Field input={<TextInput />} name="VisualDescription" />
        </ListFieldSection>
      ) : (
        <>
          <ListFieldSection>
            <Field
              input={<TextOutput />}
              blankReplacement="Additional visual description not required"
              name="VisualDescription"
            />
          </ListFieldSection>
          <ButtonGroup>
            <Button
              onClick={() => {
                setAdditionalDescriptionEditable(true);
              }}
            >
              Change
            </Button>
          </ButtonGroup>
        </>
      )}
      <ListFieldSection>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="dynamic-cone-penetrometer"
          name="DynamicConePenetrometer"
          importModel={{ Id: dcpId }}
        />
        <Field
          input={<RadioGroup />}
          name="PenetrationDepthMeasuredBy"
          options={[
            { value: PenetrationDepthMeasuredBy.MetreScale, label: 'Metre scale' },
            {
              value: PenetrationDepthMeasuredBy.GraduationsAtUniformIntervals,
              label: 'Graduations on  equipment at uniform intervals',
            },
          ]}
        />
      </ListFieldSection>
      <ListFieldSection>
        <Field
          input={<WorksheetEquipmentDropdown />}
          equipmentType="measure"
          visible={canShowMeasure}
          name="Measure"
          importModel={{ Id: measureId }}
        />
        <Field input={<NumericInput />} name="MeasurementAtZeroBlows" />
        <Field input={<NumericInput />} visible={canShowGraduationsInterval} name="GraduationInterval" />
        <Field input={<NumericInput />} name="ReducedLevelToLayer" />
        <Field input={<NumericInput />} name="SurfaceLevelToLayer" />
      </ListFieldSection>
      <ListFieldSection>
        <FieldSet>
          <Field
            input={<Checkbox />}
            label="Record ground water table location"
            name="RecordGroundWaterTableLocation"
          />
          <Field input={<NumericInput />} visible={canShowGroundWaterTable} name="GroundWaterTableDepth" />
        </FieldSet>
        <Field
          input={<RadioGroup />}
          name="MoistureCondition"
          options={[
            { value: MoistureCondition.Dry, label: 'Dry' },
            { value: MoistureCondition.Wet, label: 'Wet' },
            { value: MoistureCondition.MoistureContent, label: 'By moisture content test' },
          ]}
        />
      </ListFieldSection>
      {labMoistureTaken && (
        <WorksheetAssociatedTestSection
          heading="Moisture Content Result"
          name="AssociatedFieldMoistureContent"
          testType="Moisture Content"
          associatedTestModel={{
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field
                    input={<TextOutput />}
                    name="AssociatedFieldMoistureContent.ImportedTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedFieldMoistureContentID,
              },
            },
            external: {
              testMethods: [ExternalTestMethods.moistureContent.AS_1289_2_1_1],
              permitOtherTestMethods: false,
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    name="AssociatedFieldMoistureContent.ExternalTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      )}
    </>
  );
};

const PreparationSection = withInjectedProps(PreparationSectionBase);
export { PreparationSection };
export default PreparationSection;
